/**
 * @fileoverview Canonical "details unconfirmed" copy + predicate for every web
 * surface that renders an event's quality placement status (WEB-451).
 *
 * The backend projects `qualityStatus` (`PREMIUM` | `STANDARD` |
 * `LOW_CONFIDENCE` | `QUARANTINED`) onto the discover/search wire view-model
 * and the event-detail payload **only when the operator-gated quality gate is
 * enabled** (`LOCI_QUALITY_GATE_ENABLED`, default-OFF). When the gate is off
 * the field is absent and the UI renders nothing — preserving today's behavior
 * until the catalog is fully scored and the operator flips the switch.
 *
 * The honest-labeling rule is intentionally narrow: a "details unconfirmed"
 * affordance is rendered **only** for the explicit value `LOW_CONFIDENCE`.
 * `PREMIUM` / `STANDARD` render nothing (a real quality is not a label), and
 * `QUARANTINED` rows are suppressed from public reads upstream so they never
 * reach the wire.
 *
 * Pure data + pure function — no React imports — so the same copy ships through
 * both RSC and client renderers without a `'use client'` boundary.
 *
 * @since WEB-451
 */

import type { QualityStatus } from '@loci/types';

/**
 * Canonical visible label for a low-confidence event. Short, scannable, and
 * honest: it signals that some event details could not be verified, without
 * implying the event is fake.
 */
export const DETAILS_UNCONFIRMED_LABEL = 'Details unconfirmed' as const;

/**
 * Canonical accessible/long-form description rendered as the badge's
 * `title` / tooltip and exposed to assistive tech. Conveys the same meaning
 * as the icon so the affordance is never color- or icon-only.
 */
export const DETAILS_UNCONFIRMED_DESCRIPTION =
  'Some details for this event could not be verified' as const;

/**
 * Single source of truth for "should this surface render the details-unconfirmed
 * affordance?".
 *
 * Returns `true` only for the explicit `LOW_CONFIDENCE` value. Crucially,
 * returns `false` for `undefined` — so the label naturally shows nothing when
 * the backend quality gate is off and the field is absent from the wire, with
 * no special-casing at the call site.
 */
export function isDetailsUnconfirmed(qualityStatus: QualityStatus | null | undefined): boolean {
  return qualityStatus === 'LOW_CONFIDENCE';
}
